/** Anything `new URLSearchParams()` accepts: a query string, entries, a record, or another URLSearchParams. */
export type SearchParamsInit = ConstructorParameters<typeof URLSearchParams>[0];

/** `?a=1&b=2` ready to append to a path, or `''` when there are no params. */
export const toQs = (init: SearchParamsInit): string => {
  const query = new URLSearchParams(init).toString();
  return query ? `?${query}` : '';
};

export const searchParamsToLower = (search: SearchParamsInit): Record<string, string> => {
  return Object.fromEntries(
    [...new URLSearchParams(search).entries()].map(([key, value]) => [key.toLowerCase(), value])
  );
};

const IGNORE_URL_PARAMS = [
  'affid',
  'backurl',
  'gad_campaignid',
  'hsa_cam',
  'landingpath',
  'lat',
  'lng',
  'lp_s3',
  'lp_s4',
  'state_code',
  'state',
  't',
  'zip',
];

export const filterSearchParams = (searchParams: Record<string, string>) => {
  return Object.fromEntries(Object.entries(searchParams).filter(([key]) => !IGNORE_URL_PARAMS.includes(key)));
};

/** Strip the embed identity token (`t`) from a URL string for logs, RUM, and persisted meta. */
export const scrubEmbedTokenFromUrl = (href: string): string => {
  try {
    const url = new URL(href);
    if (!url.searchParams.has('t')) {
      return href;
    }
    url.searchParams.delete('t');
    return url.toString();
  } catch {
    return href
      .replace(/([?&])t=[^&]*/g, '')
      .replace(/\?&/, '?')
      .replace(/[?&]$/, '');
  }
};

export const isProdHost = (hostname: string | undefined) => {
  return hostname && /^\w+\.aurorasolar\.com$/.test(hostname);
};

export const isHomeSolar = (path: string) => path.startsWith('/home-solar');

// Only /home-solar/** is proxied on aurorasolar.com, so quote-site URLs must be absolute there;
// elsewhere they stay relative to the current deployment (incl. Vercel previews).
export const getQuoteBaseUrl = (pathname: string) =>
  isHomeSolar(pathname) ? process.env.NEXT_PUBLIC_BASE_URL || 'https://quote.aurorasolar.com' : '';
